import { useTranslations } from "@zapier/i18n";
import { useState } from "react";
import { type Interface } from "@zapier/client";

import {
  Alert,
  AlertDescription,
  Button,
  ModalDialogBody,
  ModalDialogFooter,
  ModalDialogHeader,
  ModalDialogRoot,
  Radio,
  RadioGroup,
  SkeletonBlock,
  Spacer,
  Text,
} from "@zapier/design-system";

import { useFetchInterfaceZaps } from "@zapier/client";
import { type DeleteInterfaceInput } from "@zapier/bff";
import { useEmitInterfaceDeleteModalEvent } from "@zapier/shared/src/events";
import { FlexSpacer } from "@zapier/shared";

import { useDeleteInterface } from "./useDeleteInterface";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  interfaceObj: Interface | undefined;
  folderId?: string; // used for cache invalidation in mutations
}

type DeleteZapsOption = DeleteInterfaceInput["related_zaps_delete_option"];

const InterfaceDeleteModal = ({
  isOpen,
  onClose,
  interfaceObj,
  folderId, // used for cache invalidation in mutations
}: Props) => {
  const t = useTranslations("interface-delete-modal");

  const [deleteZapsOption, setDeleteZapsOption] = useState<
    DeleteZapsOption | undefined
  >("disable-zap");

  const { interfaceZaps, isLoading, isFetching } = useFetchInterfaceZaps(
    {
      id: interfaceObj?.id ?? "",
    },
    { enabled: !!interfaceObj?.id && isOpen },
  );

  const { deleteInterface, isPending } = useDeleteInterface({
    interfaceObject: interfaceObj,
    // Only pass deleteZapsOption if there are Zaps connected to the Interface
    deleteZapsOption: interfaceZaps.length > 0 ? deleteZapsOption : undefined,
    folderId,
  });

  const { emit } = useEmitInterfaceDeleteModalEvent();

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      emit("cancel");
      onClose();
      setDeleteZapsOption(undefined);
    }
  };

  if (!interfaceObj) {
    return;
  }

  return (
    <ModalDialogRoot isOpen={isOpen} onOpenChange={handleOpenChange}>
      <>
        <ModalDialogHeader>
          {t("headings.delete-interface", { interfaceName: interfaceObj.name })}
        </ModalDialogHeader>
        <ModalDialogBody>
          {isLoading || isFetching ? (
            <SkeletonBlock height={130} width="100%" />
          ) : (
            <>
              <Text color="TextWeaker">
                {t("messages.permanent-deletion-warning")}
              </Text>
              <Spacer height={10} />
              {interfaceZaps?.length > 0 && (
                <>
                  <RadioGroup
                    label={t("labels.connected-zaps-action", {
                      count: interfaceZaps?.length,
                    })}
                    defaultValue="disable-zap"
                    onChange={(value) =>
                      setDeleteZapsOption(value as DeleteZapsOption)
                    }
                  >
                    <Radio value="disable-zap">
                      {t("radio-options.keep-zaps-turn-off", {
                        count: interfaceZaps?.length,
                      })}
                    </Radio>
                    <Radio value="delete-zap">
                      {t("radio-options.delete-zaps", {
                        count: interfaceZaps?.length,
                      })}
                    </Radio>
                  </RadioGroup>
                  {deleteZapsOption === "delete-zap" && (
                    <>
                      <Spacer height={10} />
                      <Alert title="" status="error">
                        <AlertDescription>
                          {t("alerts.zaps-will-be-deleted", {
                            count: interfaceZaps?.length,
                          })}
                        </AlertDescription>
                      </Alert>
                    </>
                  )}
                </>
              )}
            </>
          )}
        </ModalDialogBody>

        <ModalDialogFooter>
          <Button
            isDisabled={isPending}
            onPress={() => {
              emit("cancel");
              onClose();
            }}
            variant="tertiary"
          >
            {t("buttons.cancel")}
          </Button>

          <FlexSpacer />

          <Button
            isDisabled={isPending || isLoading || isFetching}
            isPending={isPending}
            onPress={() => {
              emit("confirm");
              deleteInterface();
              onClose();
            }}
            variant="danger"
          >
            {deleteZapsOption === "delete-zap"
              ? t("buttons.delete-with-zaps")
              : t("buttons.delete-only")}
          </Button>
        </ModalDialogFooter>
      </>
    </ModalDialogRoot>
  );
};

export default InterfaceDeleteModal;
